import { useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { uid } from "@/lib/uid";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, Loader2, Video, Trash2, Plus, ChevronDown, ChevronRight } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const LEVEL_STYLES = {
  verde: "bg-emerald-100 text-emerald-700",
  giallo: "bg-amber-100 text-amber-700",
  rosso_lieve: "bg-orange-100 text-orange-700",
  rosso: "bg-rose-100 text-rose-700",
};

function emptyContext() {
  return { forza_borg: 3, durata_min: 60, descrizione_movimento: "", arto: "destro" };
}

/**
 * Enhanced OCRA module — multi-video multitask (ISO 11228-3).
 */
export default function StepOcraMultitask({ data, onChange, aiLoading, setAiLoading, mansioniOptions = [] }) {
  const list = useMemo(() => (Array.isArray(data) ? data : []), [data]);
  const tasks = useMemo(() => list.filter((x) => x?.metodo === "ocra_multitask"), [list]);
  const legacy = useMemo(() => list.filter((x) => x?.metodo !== "ocra_multitask"), [list]);

  const [pending, setPending] = useState({
    task_name: "",
    descrizione: "",
    mansione: "",
    durata_turno_min: 480,
    durata_pause_min: 30,
    ambiente: "",
    note: "",
    videos: [],
    contesti: [],
  });
  const fileRef = useRef(null);

  const addVideos = (files) => {
    const arr = Array.from(files || []);
    setPending((p) => ({
      ...p,
      videos: [...p.videos, ...arr],
      contesti: [...p.contesti, ...arr.map(emptyContext)],
    }));
  };
  const removeVideo = (i) => setPending((p) => ({
    ...p,
    videos: p.videos.filter((_, idx) => idx !== i),
    contesti: p.contesti.filter((_, idx) => idx !== i),
  }));
  const updateContesto = (i, patch) => setPending((p) => ({
    ...p,
    contesti: p.contesti.map((c, idx) => (idx === i ? { ...c, ...patch } : c)),
  }));

  const analyze = async () => {
    if (!pending.task_name.trim()) return toast.error("Inserisci il nome del task");
    if (pending.videos.length === 0) return toast.error("Carica almeno un video");
    setAiLoading(true);
    const fd = new FormData();
    fd.append("task_name", pending.task_name);
    fd.append("descrizione", pending.descrizione);
    fd.append("mansione", pending.mansione);
    fd.append("durata_turno_min", String(pending.durata_turno_min));
    fd.append("durata_pause_min", String(pending.durata_pause_min));
    fd.append("ambiente", pending.ambiente);
    fd.append("note", pending.note);
    fd.append("contesto_json", JSON.stringify(pending.contesti));
    pending.videos.forEach((v) => fd.append("files", v));
    try {
      const r = await api.post("/dvr/ai/ocra-multitask", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 600000,
      });
      onChange([...list, { ...r.data, _uid: uid() }]);
      setPending({
        task_name: "", descrizione: "", mansione: "",
        durata_turno_min: 480, durata_pause_min: 30, ambiente: "", note: "",
        videos: [], contesti: [],
      });
      toast.success("Task OCRA multitask analizzato");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore AI");
    }
    setAiLoading(false);
  };

  const removeTask = (uidToRemove) => onChange(list.filter((t) => t._uid !== uidToRemove));

  return (
    <div className="space-y-6">
      <div className="text-sm text-muted-foreground">
        Metodo OCRA multitask (ISO 11228-3) — carica <b>più video di movimenti/cicli</b> ripetitivi arti superiori.
        L'AI valuta ogni ciclo e calcola l'<b>OCRA multitask</b> pesato sulle durate.
      </div>

      <div className="border border-border rounded-lg p-4 bg-muted/30 space-y-3" data-testid="ocra-mt-form">
        <div className="font-semibold text-sm">Nuovo task multitask</div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Nome task *</Label>
            <Input
              value={pending.task_name}
              onChange={(e) => setPending({ ...pending, task_name: e.target.value })}
              placeholder="Es. Assemblaggio schede elettroniche"
              data-testid="ocra-mt-name"
            />
          </div>
          <div>
            <Label>Mansione</Label>
            {mansioniOptions.length > 0 ? (
              <select
                className="w-full border border-border rounded-md h-9 px-2 text-sm bg-background"
                value={pending.mansione}
                onChange={(e) => setPending({ ...pending, mansione: e.target.value })}
                data-testid="ocra-mt-mansione"
              >
                <option value="">— seleziona —</option>
                {mansioniOptions.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            ) : (
              <Input
                value={pending.mansione}
                onChange={(e) => setPending({ ...pending, mansione: e.target.value })}
                data-testid="ocra-mt-mansione"
              />
            )}
          </div>
        </div>

        <div>
          <Label>Descrizione del task</Label>
          <Textarea rows={2} value={pending.descrizione}
            onChange={(e) => setPending({ ...pending, descrizione: e.target.value })}
            placeholder="Descrivi in dettaglio i cicli lavorativi: quali movimenti, con quali strumenti, con quale forza…"
            data-testid="ocra-mt-descrizione"
          />
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label>Turno (min)</Label>
            <Input type="number" value={pending.durata_turno_min}
              onChange={(e) => setPending({ ...pending, durata_turno_min: Number(e.target.value) })}
              data-testid="ocra-mt-turno"
            />
          </div>
          <div>
            <Label>Pause totali (min)</Label>
            <Input type="number" value={pending.durata_pause_min}
              onChange={(e) => setPending({ ...pending, durata_pause_min: Number(e.target.value) })}
              data-testid="ocra-mt-pause"
            />
          </div>
          <div>
            <Label>Ambiente</Label>
            <Input value={pending.ambiente}
              onChange={(e) => setPending({ ...pending, ambiente: e.target.value })}
              placeholder="Es. linea produzione"
              data-testid="ocra-mt-ambiente"
            />
          </div>
        </div>

        <div>
          <Label>Note operatore</Label>
          <Textarea rows={2} value={pending.note}
            onChange={(e) => setPending({ ...pending, note: e.target.value })}
            data-testid="ocra-mt-note"
          />
        </div>

        <div className="border-t border-border pt-3">
          <div className="flex items-center justify-between mb-2">
            <div className="font-semibold text-sm">Video movimenti ({pending.videos.length})</div>
            <Button variant="outline" size="sm" className="rounded-full"
              onClick={() => fileRef.current?.click()}
              data-testid="ocra-mt-add-video">
              <Plus className="w-4 h-4 mr-1" /> Aggiungi video
            </Button>
            <input ref={fileRef} type="file" accept="video/*" multiple hidden
              onChange={(e) => addVideos(e.target.files)} />
          </div>

          {pending.videos.length === 0 ? (
            <button onClick={() => fileRef.current?.click()}
              className="w-full border-2 border-dashed border-border rounded-md p-6 text-sm text-muted-foreground hover:bg-muted/30 flex flex-col items-center gap-2"
              data-testid="ocra-mt-upload-zone"
            >
              <Video className="w-6 h-6" />
              Trascina o clicca per caricare uno o più video di cicli ripetitivi
            </button>
          ) : (
            <div className="space-y-2">
              {pending.videos.map((v, i) => (
                <div key={v.name + i} className="border border-border rounded-md p-3 bg-background">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-sm font-semibold truncate">
                      #{i + 1} {v.name} <span className="text-xs text-muted-foreground">({(v.size / 1024 / 1024).toFixed(1)} MB)</span>
                    </div>
                    <button onClick={() => removeVideo(i)}
                      className="text-muted-foreground hover:text-destructive"
                      data-testid={`ocra-mt-remove-${i}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="grid grid-cols-4 gap-2">
                    <div className="col-span-2">
                      <Label className="text-xs">Descrizione movimento</Label>
                      <Input value={pending.contesti[i]?.descrizione_movimento || ""}
                        onChange={(e) => updateContesto(i, { descrizione_movimento: e.target.value })}
                        placeholder="Es. avvitare vite con avvitatore"
                        className="h-8 text-xs" />
                    </div>
                    <div>
                      <Label className="text-xs">Arto</Label>
                      <select className="w-full h-8 text-xs border border-border rounded-md px-2 bg-background"
                        value={pending.contesti[i]?.arto || "destro"}
                        onChange={(e) => updateContesto(i, { arto: e.target.value })}>
                        <option value="destro">Destro</option>
                        <option value="sinistro">Sinistro</option>
                        <option value="entrambi">Entrambi</option>
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs">Forza (Borg 0-10)</Label>
                      <Input type="number" step="0.5"
                        value={pending.contesti[i]?.forza_borg ?? 3}
                        onChange={(e) => updateContesto(i, { forza_borg: Number(e.target.value) })}
                        className="h-8 text-xs" />
                    </div>
                    <div className="col-span-4">
                      <Label className="text-xs">Durata sottotask nel turno (min)</Label>
                      <Input type="number"
                        value={pending.contesti[i]?.durata_min ?? 60}
                        onChange={(e) => updateContesto(i, { durata_min: Number(e.target.value) })}
                        className="h-8 text-xs" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end">
          <Button onClick={analyze}
            disabled={aiLoading || pending.videos.length === 0 || !pending.task_name.trim()}
            className="rounded-full" data-testid="ocra-mt-analyze"
          >
            {aiLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
            Analizza task ({pending.videos.length} video)
          </Button>
        </div>
      </div>

      <div className="space-y-4">
        {tasks.map((t) => (
          <TaskCard key={t._uid} task={t} onRemove={() => removeTask(t._uid)} />
        ))}
        {legacy.length > 0 && (
          <details className="text-xs text-muted-foreground border border-dashed border-border rounded p-2">
            <summary className="cursor-pointer">Analisi legacy singolo video ({legacy.length})</summary>
            <div className="pt-2 space-y-1">
              {legacy.map((n, i) => (
                <div key={n._uid || `legacy-${i}`}>
                  OCRA = {n.OCRA_index ?? "?"} · {n.fascia || "—"} · {n.commento?.slice(0, 80) || ""}
                </div>
              ))}
            </div>
          </details>
        )}
      </div>
    </div>
  );
}

function TaskCard({ task, onRemove }) {
  const [open, setOpen] = useState(false);
  const s = task.sintesi || {};
  const level = (s.livello_rischio || "giallo").toLowerCase();
  return (
    <div className="border border-border rounded-lg overflow-hidden" data-testid={`ocra-mt-task-${task._uid}`}>
      <div className="bg-slate-900 text-white px-4 py-3 flex items-center justify-between">
        <div className="flex-1">
          <div className="font-semibold">{task.task_name}</div>
          <div className="text-xs opacity-70">
            {task.mansione ? `Mansione: ${task.mansione} · ` : ""}
            {(task.movimenti || []).length} movimenti · turno {task.durata_turno_min} min
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-xs px-2 py-1 rounded ${LEVEL_STYLES[level] || "bg-slate-200 text-slate-700"}`}>
            OCRA MT {s.OCRA_multitask?.toFixed?.(2) ?? "?"} · {level.replace("_", " ").toUpperCase()}
          </span>
          <button onClick={() => setOpen(!open)} className="text-white/80 hover:text-white">
            {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
          <button onClick={onRemove} className="text-white/80 hover:text-rose-300" data-testid={`ocra-mt-task-remove-${task._uid}`}>
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
      {open && (
        <div className="p-4 space-y-4 bg-background">
          {(task.movimenti || []).map((sub, i) => (
            <SubtaskBlock key={`o-${i}`} sub={sub} idx={i + 1} />
          ))}
          {s.commento_globale && (
            <div className="p-3 bg-muted/40 rounded border-l-4 border-slate-700">
              <div className="text-xs font-semibold mb-1">Commento tecnico globale</div>
              <div className="text-sm">{s.commento_globale}</div>
            </div>
          )}
          {s.misure_preventive?.length > 0 && (
            <div>
              <div className="text-xs font-semibold mb-1">Misure preventive (ISO 11228-3)</div>
              <ul className="text-sm list-disc pl-6 space-y-1">
                {s.misure_preventive.map((m, i) => <li key={`m-${i}`}>{m}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SubtaskBlock({ sub, idx }) {
  const level = (sub.livello_rischio || "giallo").toLowerCase();
  return (
    <div className="border border-border rounded-md p-3 bg-muted/20">
      <div className="flex items-center justify-between mb-2">
        <div className="font-semibold text-sm">
          Movimento {idx}: {sub.descrizione_movimento || "—"} <span className="text-xs opacity-60">({sub.arto_analizzato})</span>
        </div>
        <span className={`text-xs px-2 py-0.5 rounded ${LEVEL_STYLES[level] || "bg-slate-200"}`}>
          OCRA {sub.OCRA_index?.toFixed?.(2) ?? "?"}
        </span>
      </div>
      {sub.frames?.length > 0 && (
        <div className="flex gap-2 overflow-x-auto mb-2">
          {sub.frames.slice(0, 4).map((u) => (
            <img key={u} src={`${BACKEND}${u}`} alt="frame" className="h-24 rounded border" />
          ))}
        </div>
      )}
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div className="bg-slate-50 rounded p-2">
          <div className="font-semibold mb-1">Parametri ciclo</div>
          <div>Azioni tec./min: {sub.azioni_tecniche_min}</div>
          <div>Durata ciclo: {sub.durata_ciclo_sec} sec · Recupero: {sub.recupero_sec} sec</div>
          <div>Forza Borg: {sub.forza_borg} · Az. raccomandate/min: {sub.azioni_raccomandate_min?.toFixed?.(1)}</div>
        </div>
        <div className="bg-slate-50 rounded p-2">
          <div className="font-semibold mb-1">Moltiplicatori</div>
          <div>Freq: {sub.moltiplicatori?.freq?.toFixed?.(2)} · Forza: {sub.moltiplicatori?.forza?.toFixed?.(2)}</div>
          <div>Postura: {sub.moltiplicatori?.postura?.toFixed?.(2)} · Recupero: {sub.moltiplicatori?.recupero?.toFixed?.(2)}</div>
        </div>
      </div>
      {sub.posture_critiche?.length > 0 && (
        <div className="mt-2">
          <div className="text-xs font-semibold text-amber-700">Posture critiche</div>
          <ul className="text-xs list-disc pl-5">
            {sub.posture_critiche.map((p, i) => <li key={`p-${i}`}>{p}</li>)}
          </ul>
        </div>
      )}
      {sub.criticita_osservate?.length > 0 && (
        <div className="mt-2">
          <div className="text-xs font-semibold text-rose-700">Criticità</div>
          <ul className="text-xs list-disc pl-5">
            {sub.criticita_osservate.map((c, i) => <li key={`c-${i}`}>{c}</li>)}
          </ul>
        </div>
      )}
      {sub.commento_tecnico && (
        <div className="text-xs mt-2 italic text-slate-700">{sub.commento_tecnico}</div>
      )}
    </div>
  );
}
